import { useTranslation } from 'react-i18next';

import type { MetricChartSpec } from '@/components/charts/generic';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import type {
    BioscopeDataQuality,
    FeedingDataSources,
    FeedingOverlaySources,
    FeedingResolution,
    FeedingSeriesPoint,
} from '@/domain/feedingDashboard';

import { DataSourcesLink } from './DataSourcesLink';
import { dashboardLocale } from './dashboardLocale';
import { FeedingChartPanel } from './FeedingChartPanel/FeedingChartPanel';
import { buildFeedWeightCoreSeries, unwiredSeriesLabels } from './feedWeightCoreSeries';
import { buildFeedWeightOverlays } from './feedWeightOverlays';
import { joinLabels, overlayCaptions } from './overlayCaptions';
import { partialWeeks } from './partialWeeks';
import { systemLabels } from './systemLabel';
import { todayMarker } from './todayMarker';

import styles from './FeedWeightChart.module.scss';

interface FeedWeightChartProps {
    series: FeedingSeriesPoint[];
    /** Customer's planning system, for the weight-curve labels (IPS req 28). */
    pmsSystemName: string | null;
    dataSources: FeedingDataSources;
    /** Where each overlay came from, so a planning-system value says so in the legend. */
    overlaySources: FeedingOverlaySources;
    bioscopeDataQuality: BioscopeDataQuality;
    /**
     * Bucket granularity the backend chose for this period (IPS req 4). Surfaced
     * because a weekly bucket is keyed by the date of its Monday, which read as "only
     * that one day was measured"; it now drives the W31 axis and the header badge.
     */
    resolution: FeedingResolution;
    /** The site's day in progress, marked on the chart; null from an older backend. */
    siteToday: string | null;
    /** The selected period start, so a weekly chart can say which weeks it cuts short. */
    periodStart: string;
    /** The selected period end: a period that ends before `siteToday` marks nothing. */
    periodEnd: string;
    loading?: boolean;
    error?: unknown;
    /** Where the not-mapped caption sends a client admin to map the series; null for no link. */
    dataSourcesHref?: string | null;
}

/**
 * Feed volume against weight over the period (IPS req 15-16), with optional
 * environmental overlays.
 *
 * A mixed chart: feed as bars on the left axis, the weight curves on the right (see
 * `buildFeedWeightCoreSeries`). `spec.kind` is `'bar'` and the weight series override it
 * per series, which is what the shared layer's `MetricSeriesSpec.type` exists for.
 *
 * Each overlay gets its own axis, untitled and coloured like its series (see
 * `buildFeedWeightOverlays`). Temperature and oxygen sit around 10 while salinity is
 * around 33, so a shared scale would flatten all three into near-straight lines. The
 * overlays start hidden and are toggled from the chart legend rather than from a bespoke
 * checkbox row, so overlay visibility works like every other series in the app, and an
 * overlay's axis is drawn only while its series is shown.
 */
export function FeedWeightChart(props: FeedWeightChartProps) {
    const {
        series,
        dataSources,
        overlaySources,
        bioscopeDataQuality,
        resolution,
        siteToday,
        periodStart,
        periodEnd,
        pmsSystemName,
        loading,
        error,
        dataSourcesHref = null,
    } = props;
    const { t, i18n } = useTranslation();
    const locale = dashboardLocale(i18n);
    const system = systemLabels(t, pmsSystemName);

    const days = series.map((point) => point.bucket);
    const today = todayMarker({ siteToday, periodEnd, resolution, buckets: days }, t);
    const partial = partialWeeks(
        { periodStart, periodEnd, siteToday, resolution, buckets: days },
        t
    );
    const chartSeries = buildFeedWeightCoreSeries(series, dataSources, system, t);
    const overlays = buildFeedWeightOverlays(series, t, overlaySources, system.name);
    chartSeries.push(...overlays.series);
    const overlayNotes = overlayCaptions({ overlays, systemName: system.name, locale }, t);

    const spec: MetricChartSpec<'bar'> = {
        kind: 'bar',
        days,
        // A weekly bucket is keyed by its Monday, so the day axis would print a single
        // date for seven days of data. 'week' renders it as W31 instead.
        xAxisKind: resolution === 'week' ? 'week' : 'day',
        locale,
        series: chartSeries,
        left: {
            label: t('feedingModule.charts.feedAxis'),
            unit: 't',
            beginAtZero: true,
            valueDecimals: 1,
        },
        // Whole grams on the ticks: "3,400.0 g" carried a decimal no reading here needs.
        right: {
            label: t('feedingModule.charts.weightAxis'),
            unit: 'g',
            tickDecimals: 0,
            valueDecimals: 0,
        },
        extraAxes: overlays.axes,
        tooltipExtraLines: partial.tooltipExtraLines,
        inProgress: today?.inProgress,
    };

    const chart = useMetricChart(spec);
    const unwired = unwiredSeriesLabels(dataSources, system, t);

    return (
        <FeedingChartPanel
            title={t('feedingModule.charts.feedWeightTitle')}
            quality={bioscopeDataQuality}
            badge={resolution === 'week' ? t('feedingModule.charts.weeklyBadge') : undefined}>
            <MetricChart
                kind="bar"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                isLoading={loading}
                error={error}
                height={340}
                // The portal's empty-chart state (IPS 2.3.2 "existing behaviour"), rather
                // than an empty grid that reads as a rendering fault.
                noDataMessage={t('feedingModule.charts.feedWeightNoData')}
            />
            {/* This chart's own buckets only: the closing-day bars and the table below
                are not weekly, and the pellet timeline says so for itself. */}
            {resolution === 'week' ? (
                <p className={styles.sourceNotice}>{t('feedingModule.charts.weeklyBuckets')}</p>
            ) : null}
            {partial.caption ? <p className={styles.sourceNotice}>{partial.caption}</p> : null}
            {today?.caption ? <p className={styles.sourceNotice}>{today.caption}</p> : null}
            {unwired.length > 0 ? (
                <p className={styles.sourceNotice}>
                    {/* Each series name carries the system, so the sentence does not. */}
                    {t('feedingModule.dataSource.seriesNotConfigured', {
                        series: joinLabels(unwired, locale),
                        count: unwired.length,
                    })}
                    <DataSourcesLink href={dataSourcesHref} labelKey="providerConfig.entry.setUp" />
                </p>
            ) : null}
            {overlayNotes.map((note) => (
                <p key={note} className={styles.sourceNotice}>
                    {note}
                </p>
            ))}
        </FeedingChartPanel>
    );
}
